<template>
  <!-- Design: JobItem -->
  <article class="ts-job-item" :data-ts-job-id="id">
    <div class="d-flex align-items-start justify-content-between gap-3">
      <h2 class="mb-0">
        <a
          class="ts-job-title fw-semibold"
          :href="`#job-${id}`"
          aria-haspopup="dialog"
          @mouseenter="translate(title)"
          @focusin="translate(title)"
          @click.prevent="open"
        >
          <span v-b-tooltip.hover="{ customClass: 'ts-tooltip' }" :title="hoverTranslation">{{ title }}</span>
        </a>
        <span
          v-if="contact"
          v-b-tooltip.hover.top="{ customClass: 'ts-tooltip' }"
          class="result-email-icon ts-mail-hint"
          role="img"
          tabindex="0"
          :title="translations[11497]"
          :aria-label="translations[11497]"
        >
          <i class="bi bi-envelope" aria-hidden="true"></i>
        </span>
      </h2>
      <button
        v-b-tooltip.hover="{ customClass: 'ts-tooltip', placement: bookmarkTooltipPlacement }"
        class="btn ts-bookmark-button d-inline-flex flex-shrink-0 align-items-center justify-content-center"
        type="button"
        :aria-pressed="String(active)"
        :title="active ? translations[14012] : translations[13596]"
        @click="toggleNotepad"
      >
        <i class="bi" :class="active ? 'bi-bookmark-check-fill' : 'bi-bookmark'" aria-hidden="true"></i>
      </button>
    </div>
    <!-- Positions in another language: the translation under the title (translation_of_positions) -->
    <p v-if="showTranslation && translateObject" class="ts-job-translation small mb-0">{{ translateObject }}</p>

    <div class="ts-job-meta d-flex flex-wrap gap-3 small mt-2">
      <span :class="{ 'user-select-none blur-5': isFirmnameDisabled }" @click="isFirmnameDisabled && $bvModal.show('not-auth-modal')">
        <i class="bi bi-building me-1" aria-hidden="true"></i>
        <span class="visually-hidden">{{ translations[13757] }}</span>
        {{ firmName }}
      </span>
      <span>
        <i class="bi bi-geo-alt me-1" aria-hidden="true"></i>
        <span class="visually-hidden">{{ translations[404] }}:</span>
        {{ location }}
      </span>
      <span class="ms-md-auto">
        <i class="bi bi-clock me-1" aria-hidden="true"></i>
        {{ date }}
      </span>
    </div>
  </article>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import GoogleTranslate from '@/mixins/GoogleTranslate';
import { AppModule } from '@/store/modules/app';
import { ProfessionModule } from '@/store/modules/profession';
import { ClustersModule } from '@/store/modules/clusters';
import { CompanyModule } from '@/store/modules/company';
import { groupCurses } from '@/config/configuration';
import { toggleJobNotepad } from '@/sections/jobs/notepad';

// One job ad of a result list. The title opens its detail panel (jobs-sidebar), the bookmark
// keeps it in the notebook — on the server when logged in, in the browser otherwise.
@Component
export default class TsJobItem extends mixins(GoogleTranslate) {
  @Prop({ required: true }) readonly id!: number;

  @Prop({ default: '' }) readonly title!: string;

  @Prop({ default: '' }) readonly firmName!: string;

  @Prop({ default: '' }) readonly location!: string;

  @Prop({ default: '' }) readonly date!: string;

  @Prop({ default: '' }) readonly shortview!: string;

  // 1 = the ad has an e-mail address to apply to
  @Prop({ default: 0 }) readonly contact!: number;

  // In the notebook
  @Prop({ type: Boolean, default: false }) readonly active!: boolean;

  @Prop({ type: Boolean, default: false }) readonly isAuth!: boolean;

  // In the job panel itself ("Weitere Stellen"): show the job there instead of toggling the panel
  @Prop({ type: Boolean, default: false }) readonly inPanel!: boolean;

  // The bookmark sits at the card's end, so its tooltip opens inwards instead of off the screen
  get bookmarkTooltipPlacement() {
    return AppModule.rtl ? 'right' : 'left';
  }

  get translations() {
    return AppModule.translation;
  }

  get isCourses() {
    return groupCurses(AppModule.sid);
  }

  get showTranslation() {
    return Boolean(this.enabledTranslate) && !this.isLang;
  }

  // Without translation_of_positions the translation shows on hover (not in German)
  get hoverTranslation() {
    return !this.enabledTranslate ? this.translateObject : '';
  }

  get isFirmnameDisabled() {
    return AppModule.settingPortal.not_logged_in_blur_employer_data && !this.isAuth;
  }

  get isJobModalDisabled() {
    return AppModule.settingPortal.not_logged_in_block_job_ads && !this.isAuth;
  }

  created() {
    if (this.showTranslation) this.allTranslate(this.title);
  }

  open() {
    if (this.isJobModalDisabled) {
      this.$bvModal.show('not-auth-modal');
      return;
    }
    ProfessionModule.SetJobId(this.id);
    ProfessionModule.SetJobFirm(this.firmName);
    ProfessionModule.SetJobShort(this.shortview);
    ClustersModule.SetUrlIframe('');
    if (this.inPanel) {
      this.$emit('opened');
      return;
    }
    CompanyModule.RESET_COMPANY_DETAIL_FILTER();
    CompanyModule.SetCompanyDetailFilter({ obj: 'idjob', value: this.id });
    this.$root.$emit('bv::toggle::collapse', 'jobs-sidebar');
  }

  async toggleNotepad() {
    // Without login it is kept in the browser; the portal asks to log in to keep it for good
    if (!this.isAuth && !this.isCourses) this.$bvModal.show('not-auth-modal');
    await toggleJobNotepad({
      id: this.id,
      title: this.title,
      firmName: this.firmName,
      location: this.location,
      date: this.date,
      shortview: this.shortview,
    });
  }
}
</script>
